import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

/** Organisation identity used across all legal pages. Single source of truth. */
export const ORG_NAME = "MEHUL Japanese Language Education Center";
export const ORG_SHORT = "MEHUL";

/**
 * Contact details shown at the foot of every legal page.
 * Single source of truth — update here if a branch, number or email changes.
 */
export const CONTACT = {
  legalName: ORG_NAME,
  /** General enquiries. */
  email: "info@mehul.com.bd",
  /** Refund, privacy and policy-related enquiries. */
  supportEmail: "support@mehul.com.bd",
  branches: [
    {
      name: "Dhaka (Head Office)",
      nameBn: "ঢাকা (প্রধান কার্যালয়)",
      phone: "01804-667050",
      phoneTel: "01804667050",
      address:
        "Kazipara (Metro Pillar 267), Union Bank 4th Floor (Lift-3), Edifice Centre, 1281 Begum Rokeya Sarani, East Monipur, Mirpur, Dhaka-1216, Bangladesh",
    },
    {
      name: "Rajshahi Branch",
      nameBn: "রাজশাহী শাখা",
      phone: "01879-062324",
      phoneTel: "01879062324",
      address:
        "94/1 (First Floor), Debisingpara, Vadra-Talaimari Road, Kazla, Rajshahi 6204, Bangladesh",
    },
    {
      name: "Mymensingh Branch",
      nameBn: "ময়মনসিংহ শাখা",
      phone: "01617-149625",
      phoneTel: "01617149625",
      address:
        "Tahmina Amin Vaban, 24 Shamcharan Roy Road, Baylen, Sadar, Mymensingh, Bangladesh",
    },
    {
      name: "Jamalpur Branch",
      nameBn: "জামালপুর শাখা",
      phone: "01616-677289",
      phoneTel: "01616677289",
      address:
        "South Kacharipara, New College Road, Jamalpur Sadar Upazila, Jamalpur-2000, Bangladesh",
    },
  ],
};

/** Effective date shown on every page. Update when policies change. */
export const EFFECTIVE_DATE = "22 July 2026";

interface LegalLayoutProps {
  title: string;
  titleBn: string;
  intro: ReactNode;
  children: ReactNode;
}

/**
 * Shared shell for all public legal / policy pages: brand header, back link,
 * effective date, the page body, a standard contact block and cross-links to
 * the other policies. Self-contained (no auth/dashboard dependency) so it works
 * for signed-out visitors.
 */
export function LegalLayout({ title, titleBn, intro, children }: LegalLayoutProps) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/60 sticky top-0 bg-background/90 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Home">
            <BrandLogo size="md" />
          </Link>
          <Link
            to="/"
            className="text-xs font-semibold text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="size-4" />
            Back to Home / হোমে ফিরুন
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="mb-8 border-b border-border/60 pb-6">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-lg text-muted-foreground font-medium mt-1">{titleBn}</p>
          <p className="text-xs text-muted-foreground mt-3">
            Effective date / কার্যকর তারিখ: <strong>{EFFECTIVE_DATE}</strong>
          </p>
        </div>

        <div className="prose-legal space-y-2 text-sm leading-relaxed text-foreground/90">
          {intro}
        </div>

        <div className="mt-8 space-y-9">{children}</div>

        {/* Contact block */}
        <section className="mt-12 border border-border rounded-2xl bg-muted/30 p-6">
          <h2 className="text-base font-bold mb-1">Contact Us / যোগাযোগ</h2>
          <p className="text-xs text-muted-foreground mb-4">
            For any question about these policies, to request a refund, or to exercise your rights,
            contact us:
            <br />
            এই নীতিমালা সম্পর্কে যেকোনো প্রশ্ন, ফেরতের আবেদন বা আপনার অধিকার প্রয়োগ করতে যোগাযোগ করুন:
          </p>

          {/* Business + email */}
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mb-5">
            <div className="sm:col-span-2">
              <dt className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Business / প্রতিষ্ঠান</dt>
              <dd className="font-medium">{CONTACT.legalName}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">General Email / সাধারণ ইমেইল</dt>
              <dd className="font-medium">
                <a href={`mailto:${CONTACT.email}`} className="hover:text-primary font-mono">{CONTACT.email}</a>
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Support / Refunds — সাপোর্ট</dt>
              <dd className="font-medium">
                <a href={`mailto:${CONTACT.supportEmail}`} className="hover:text-primary font-mono">{CONTACT.supportEmail}</a>
              </dd>
            </div>
          </dl>

          {/* Branches */}
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">
            Branches / শাখাসমূহ
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CONTACT.branches.map((b) => (
              <div key={b.name} className="border border-border/60 rounded-xl bg-background/60 p-3.5">
                <div className="font-semibold text-sm">
                  {b.name} <span className="text-muted-foreground font-normal">/ {b.nameBn}</span>
                </div>
                <a
                  href={`tel:${b.phoneTel}`}
                  className="text-xs font-mono text-primary hover:underline inline-block mt-0.5"
                >
                  {b.phone}
                </a>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{b.address}</p>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-muted-foreground mt-5">
            Consumer complaints may also be lodged with the Directorate of National Consumer
            Rights Protection (DNCRP), Bangladesh.
            <br />
            ভোক্তা অভিযোগ জাতীয় ভোক্তা-অধিকার সংরক্ষণ অধিদপ্তর (ডিএনসিআরপি), বাংলাদেশ-এও দাখিল করা যাবে।
          </p>
        </section>

        {/* Cross-links */}
        <nav className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
          <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-primary transition-colors">Terms &amp; Conditions</Link>
          <Link to="/refund-policy" className="hover:text-primary transition-colors">Refund Policy</Link>
          <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookie Policy</Link>
        </nav>

        <p className="mt-8 text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} {ORG_NAME}. All rights reserved. / সর্বস্বত্ব সংরক্ষিত।
        </p>
      </main>
    </div>
  );
}

/** A numbered clause block: heading (bilingual) + body. Keeps pages consistent. */
export function Clause({
  n,
  title,
  titleBn,
  children,
}: {
  n: string;
  title: string;
  titleBn: string;
  children: ReactNode;
}) {
  return (
    <section className="scroll-mt-20">
      <h2 className="text-base md:text-lg font-bold flex items-baseline gap-2">
        <span className="text-primary font-mono text-sm">{n}.</span>
        <span>
          {title} <span className="text-muted-foreground font-medium">/ {titleBn}</span>
        </span>
      </h2>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-foreground/90 pl-6">
        {children}
      </div>
    </section>
  );
}

/** English paragraph followed by its Bangla rendering, visually paired. */
export function Bi({ en, bn }: { en: ReactNode; bn: ReactNode }) {
  return (
    <div className="space-y-1">
      <p>{en}</p>
      <p className="text-foreground/70">{bn}</p>
    </div>
  );
}

/** Bilingual bulleted list. Each item is [English, Bangla]. */
export function BiList({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <ul className="space-y-2 list-disc pl-5">
      {items.map(([en, bn], i) => (
        <li key={i}>
          <span>{en}</span>
          <br />
          <span className="text-foreground/70">{bn}</span>
        </li>
      ))}
    </ul>
  );
}
